import { SHIPPING_COUNTRY_CODES } from "@/lib/siteconfig/sitemap-siteconfig"
import {
    type Address,
    type ShippingAddress
} from "@/types/centra/checkout-api/payment/payment-reciept"
import { type CountryWithStates } from "@/types/centra/checkout-api/selection/get-selection"

export default function AddressInformation({
    getShippingAddress = true,
    shippingAddress,
    billingAddress,
    countriesWithStates,
    isNAMarket
}: {
    getShippingAddress?: boolean
    shippingAddress?: ShippingAddress
    // `state` is optional: the Centra selection address has it optional, the receipt
    // address requires it — both callers satisfy this without a cast.
    billingAddress?: Omit<Address, "state"> & { state?: string }
    countriesWithStates: CountryWithStates[]
    isNAMarket: boolean
}) {
    const shippingAddressCountry = countriesWithStates.find(
        (country: any) => country.country === shippingAddress?.country
    )
    const billingAddressCountry = countriesWithStates.find(
        (country: any) => country.country === billingAddress?.country
    )
    const shippingAddressStateName = shippingAddressCountry?.states?.find(
        (state: any) => state.state === shippingAddress?.state
    )
    const billingAddressStateName = billingAddressCountry?.states?.find(
        (state: any) => state.state === billingAddress?.state
    )

    const isShippingAddressUSA =
        shippingAddressCountry?.country.toLowerCase() === SHIPPING_COUNTRY_CODES.USA
    const isBillingAddressUSA =
        billingAddressCountry?.country.toLowerCase() === SHIPPING_COUNTRY_CODES.USA

    const shippingAddressStateAndCountry =
        isShippingAddressUSA &&
        shippingAddressStateName?.state &&
        shippingAddressCountry?.name
            ? `, ${shippingAddressStateName?.state}`
            : shippingAddressStateName?.state
              ? `, ${shippingAddressStateName?.state}`
              : null

    const billingAddressStateAndCountry =
        isBillingAddressUSA &&
        billingAddressStateName?.state &&
        billingAddressCountry?.name
            ? `, ${billingAddressStateName?.state}`
            : billingAddressStateName?.state
              ? `, ${billingAddressStateName?.state}`
              : null

    const inlineStyle = {
        color: "inherit",
        textDecoration: "none"
    }

    return getShippingAddress ? (
        <>
            <span
                className="!no-underline"
                style={inlineStyle}
                {...({ "x-apple-data-detectors": "false" } as any)}
            >
                {shippingAddress?.email}
            </span>
            <span
                className="!no-underline"
                style={inlineStyle}
                {...({ "x-apple-data-detectors": "false" } as any)}
            >
                {shippingAddress?.firstName} {shippingAddress?.lastName}
            </span>
            <span
                className="!no-underline"
                style={inlineStyle}
                {...({ "x-apple-data-detectors": "false" } as any)}
            >
                {shippingAddress?.address1}
            </span>
            {isNAMarket ? (
                <span
                    className="!no-underline"
                    style={inlineStyle}
                    {...({ "x-apple-data-detectors": "false" } as any)}
                >
                    {shippingAddress?.city}
                    {shippingAddressStateAndCountry
                        ? shippingAddressStateAndCountry
                        : null}
                    &nbsp;{shippingAddress?.zipCode}
                </span>
            ) : (
                <span
                    className="!no-underline"
                    style={inlineStyle}
                    {...({ "x-apple-data-detectors": "false" } as any)}
                >
                    {shippingAddress?.zipCode} {shippingAddress?.city}
                    {shippingAddressStateAndCountry
                        ? shippingAddressStateAndCountry
                        : null}
                </span>
            )}
            <span
                className="!no-underline"
                style={inlineStyle}
                {...({ "x-apple-data-detectors": "false" } as any)}
            >
                {shippingAddress?.phoneNumber}
            </span>
            {!isShippingAddressUSA && (
                <span
                    className="!no-underline"
                    style={inlineStyle}
                    {...({ "x-apple-data-detectors": "false" } as any)}
                >
                    {shippingAddressCountry?.name}
                </span>
            )}
        </>
    ) : (
        <>
            <span
                className="!no-underline"
                style={inlineStyle}
                {...({ "x-apple-data-detectors": "false" } as any)}
            >
                {billingAddress?.firstName} {billingAddress?.lastName}
            </span>
            <span
                className="!no-underline"
                style={inlineStyle}
                {...({ "x-apple-data-detectors": "false" } as any)}
            >
                {billingAddress?.address1}
            </span>
            {isNAMarket ? (
                <span
                    className="!no-underline"
                    style={inlineStyle}
                    {...({ "x-apple-data-detectors": "false" } as any)}
                >
                    {billingAddress?.city}
                    {billingAddressStateAndCountry ? billingAddressStateAndCountry : null}
                    &nbsp;{billingAddress?.zipCode}
                </span>
            ) : (
                <span
                    className="!no-underline"
                    style={inlineStyle}
                    {...({ "x-apple-data-detectors": "false" } as any)}
                >
                    {billingAddress?.zipCode} {billingAddress?.city}
                    {billingAddressStateAndCountry ? billingAddressStateAndCountry : null}
                </span>
            )}
            <span
                className="!no-underline"
                style={inlineStyle}
                {...({ "x-apple-data-detectors": "false" } as any)}
            >
                {billingAddress?.phoneNumber}
            </span>
            {!isBillingAddressUSA && (
                <span
                    className="!no-underline"
                    style={inlineStyle}
                    {...({ "x-apple-data-detectors": "false" } as any)}
                >
                    {billingAddressCountry?.name}
                </span>
            )}
        </>
    )
}
